(() => {
const VIEWPORT_SETTLEMENT_MS = 160;

const isRenderedInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
};

class FsAdaptiveMedia extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-fs-media-toggle]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.saveData = Boolean(navigator.connection?.saveData);
    this.userPaused = false;
    this.mobile = window.matchMedia('(max-width: 749px)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && (!this.mobile || this.dataset.autoplayMobile === 'true');
    this.onToggle = this.onToggle.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.onViewportActivity = this.onViewportActivity.bind(this);

    this.dataset.videoState = 'poster';
    this.dataset.dataSaver = String(this.saveData);
    this.toggle?.addEventListener('click', this.onToggle);
    this.reducedMotion.addEventListener?.('change', this.onMediaChange);
    document.addEventListener('shopify:section:select', this.onSectionSelect);

    if (!this.video || this.saveData || !this.autoplay) return;
    if (this.dataset.priority === 'true') {
      requestAnimationFrame(() => requestAnimationFrame(() => this.prepare()));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((candidate) => candidate.target === this);
        if (!entry) return;
        this.inView = entry.isIntersecting;
        if (!this.inView) {
          clearTimeout(this.viewportTimer);
          return;
        }
        this.scheduleViewportSettlement();
      },
      { rootMargin: '0px' },
    );
    this.observer.observe(this);
    window.addEventListener('scroll', this.onViewportActivity, { passive: true });
  }

  disconnectedCallback() {
    clearTimeout(this.viewportTimer);
    this.pause();
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onViewportActivity);
    this.toggle?.removeEventListener('click', this.onToggle);
    this.reducedMotion?.removeEventListener?.('change', this.onMediaChange);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
  }

  scheduleViewportSettlement() {
    clearTimeout(this.viewportTimer);
    this.viewportTimer = setTimeout(() => {
      if (!this.inView || !isRenderedInViewport(this)) return;
      this.prepare();
    }, VIEWPORT_SETTLEMENT_MS);
  }

  onViewportActivity() {
    if (this.inView) this.scheduleViewportSettlement();
  }

  prepare(forcePlayback = false) {
    if (!this.video || (this.saveData && !forcePlayback)) return;
    if (!this.video.querySelector('source')) this.attachSources();
    clearTimeout(this.viewportTimer);
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onViewportActivity);
    if (forcePlayback || (this.autoplay && !this.reducedMotion.matches && !this.userPaused)) this.play();
  }

  attachSources() {
    const mobileTemplate = this.querySelector('template[data-fs-mobile-sources]');
    const desktopTemplate = this.querySelector('template[data-fs-desktop-sources]');
    const useMobile = this.mobile && mobileTemplate?.content.children.length;
    const template = useMobile ? mobileTemplate : desktopTemplate;
    if (!template) return;
    this.video.append(template.content.cloneNode(true));
    this.video.load();
    this.dataset.videoState = 'ready';
  }

  async play() {
    if (!this.video) return;
    try {
      await this.video.play();
      this.dataset.videoState = 'playing';
      this.toggle?.setAttribute('aria-label', this.toggle.dataset.pauseLabel);
    } catch (_error) {
      this.dataset.videoState = 'blocked';
      this.toggle?.setAttribute('aria-label', this.toggle.dataset.playLabel);
    }
  }

  pause() {
    this.video?.pause();
    if (this.dataset.videoState === 'playing') this.dataset.videoState = 'paused';
    this.toggle?.setAttribute('aria-label', this.toggle.dataset.playLabel);
  }

  onToggle() {
    if (!this.video?.querySelector('source')) {
      this.userPaused = false;
      this.prepare(true);
      return;
    }
    if (this.video.paused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.pause();
    }
  }

  onMediaChange(event) {
    if (event.matches) this.pause();
    else if (this.autoplay && !this.userPaused && !this.saveData) this.prepare();
  }

  onSectionSelect(event) {
    if (event.target.contains(this)) this.pause();
  }
}

if (!customElements.get('fs-adaptive-media')) {
  customElements.define('fs-adaptive-media', FsAdaptiveMedia);
}

class FsControlledVideo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-fs-controlled-toggle]');
    this.poster = this.querySelector('.fs-controlled-video__poster:not(.fs-controlled-video__poster--no-js)');
    this.ambient = this.dataset.ambient === 'true';
    this.themeControlled = this.dataset.themeControlled === 'true';
    this.customControl = this.ambient || this.themeControlled;
    this.userPaused = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.onPlaybackIntent = this.onPlaybackIntent.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onMediaError = this.onMediaError.bind(this);
    this.onViewportActivity = this.onViewportActivity.bind(this);
    document.addEventListener('shopify:section:select', this.onSectionSelect);

    if (this.customControl) {
      this.dataset.videoState = 'poster';
      this.toggle?.removeAttribute('hidden');
      this.toggle?.addEventListener('click', this.onToggle);
      this.reducedMotion.addEventListener?.('change', this.onMediaChange);
      this.video?.addEventListener('error', this.onMediaError);
    } else {
      this.video?.addEventListener('pointerdown', this.onPlaybackIntent);
      this.video?.addEventListener('keydown', this.onPlaybackIntent);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((candidate) => candidate.target === this);
        if (!entry) return;
        this.inView = entry.isIntersecting;
        if (!this.inView) {
          clearTimeout(this.viewportTimer);
          if (this.ambient) this.pause();
          return;
        }
        if (this.customControl) {
          this.scheduleViewportSettlement();
          return;
        }
        this.hydrate();
      },
      { rootMargin: this.customControl ? '0px' : '600px 0px' },
    );
    this.observer.observe(this);
    if (this.customControl) window.addEventListener('scroll', this.onViewportActivity, { passive: true });
  }

  disconnectedCallback() {
    clearTimeout(this.viewportTimer);
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onViewportActivity);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    this.video?.removeEventListener('pointerdown', this.onPlaybackIntent);
    this.video?.removeEventListener('keydown', this.onPlaybackIntent);
    this.toggle?.removeEventListener('click', this.onToggle);
    this.reducedMotion?.removeEventListener?.('change', this.onMediaChange);
    this.video?.removeEventListener('error', this.onMediaError);
  }

  scheduleViewportSettlement() {
    clearTimeout(this.viewportTimer);
    this.viewportTimer = setTimeout(() => {
      if (!this.inView || !isRenderedInViewport(this)) return;
      this.hydrate();
      if (this.ambient && !this.reducedMotion.matches && !this.userPaused) this.play();
    }, VIEWPORT_SETTLEMENT_MS);
  }

  onViewportActivity() {
    if (this.customControl && this.inView) this.scheduleViewportSettlement();
  }

  hydrate() {
    if (!this.video || this.dataset.hydrated === 'true') return;
    this.upgradePoster();
    if (this.dataset.poster) this.video.poster = this.dataset.poster;
    this.dataset.hydrated = 'true';
    if (!this.ambient) {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onViewportActivity);
    }
  }

  upgradePoster() {
    if (!this.customControl || !this.poster || this.poster.dataset.posterResolution === 'responsive') return;
    const srcset = this.dataset.responsivePosterSrcset;
    if (!srcset) return;
    this.poster.sizes = this.dataset.responsivePosterSizes || '100vw';
    this.poster.srcset = srcset;
    this.poster.dataset.posterResolution = 'responsive';
  }

  preparePlayback() {
    if (!this.video || this.video.querySelector('source')) return;
    this.hydrate();
    const template = this.querySelector('template[data-fs-video-sources]');
    if (template) this.video.append(template.content.cloneNode(true));
    this.video.load();
  }

  async play() {
    if (!this.video) return;
    this.preparePlayback();
    if (!this.video.querySelector('source')) {
      this.onMediaError();
      return;
    }
    try {
      await this.video.play();
      this.dataset.videoState = 'playing';
      this.toggle?.setAttribute('aria-label', this.toggle.dataset.pauseLabel);
    } catch (_error) {
      this.dataset.videoState = 'blocked';
      this.toggle?.setAttribute('aria-label', this.toggle.dataset.playLabel);
    }
  }

  pause() {
    this.video?.pause();
    if (this.dataset.videoState === 'playing') this.dataset.videoState = 'paused';
    this.toggle?.setAttribute('aria-label', this.toggle.dataset.playLabel);
  }

  onPlaybackIntent(event) {
    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
    this.preparePlayback();
  }

  onToggle() {
    if (this.video?.paused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.pause();
    }
  }

  onMediaChange(event) {
    if (event.matches) this.pause();
    else if (this.ambient && this.inView && !this.userPaused) this.play();
  }

  onMediaError() {
    this.pause();
    this.dataset.videoState = 'unavailable';
    this.toggle?.setAttribute('hidden', '');
  }

  onSectionSelect(event) {
    if (!event.target.contains(this)) return;
    this.hydrate();
    if (this.ambient) this.pause();
  }
}

if (!customElements.get('fs-controlled-video')) {
  customElements.define('fs-controlled-video', FsControlledVideo);
}
})();
